import React, { ReactNode } from 'react';
import {
  LINK_ALL_METHOD_PDF,
  LINK_BILLING_METHOD_PDF,
  LINK_BILLING_METHOD_VIDEO,
  LINK_BILLING_METHOD_VIDEO_HYOSUNG,
  LINK_PAY_METHOD_VIDEO,
} from './constants';
import { Box } from '@mui/material';

export type TossOnboardingResponse = {
  pass?: boolean;
  message?: string | TossOnboardingInfo | null;
  code?: string | null;
  errorCode?: string | null;
  error?: string | null;
  orgMessage?: string | null;
  statusCode?: number | null;
};

export type TossBillingOnboardingInfo = {
  contractStatus?: string | null;
  credentialsSynced?: boolean | null;
  enabled?: boolean | null;
  exists?: boolean | null;
};

export type TossOnboardingInfo = {
  archivedCid?: number | null;
  archivedShopId?: string | null;
  exists?: boolean | null;
  shopId?: string | null;
  shopName?: string | null;
  businessRegistrationNumber?: string | null;
  shopContactName?: string | null;
  shopContactPhoneNumber?: string | null;
  mid?: string | null;
  applyUrl?: string | null;
  applyUrlExpiresInSeconds?: number | null;
  onboardingStatus?: string | null;
  contractStatus?: string | null;
  modifying?: number | boolean | null;
  dashboardUrl?: string | null;
  feeStatus?: string | null;
  feePaidAt?: string | null;
  credentialsSynced?: boolean | null;
  credentialsSyncError?: string | null;
  errorCode?: string | null;
  code?: string | null;
  error?: string | null;
  previousShopId?: string | null;
  reset?: boolean | null;
  billingOnboarding?: TossBillingOnboardingInfo | null;
  modalViewed?: 1 | null;
};

export type AccountServiceStatus = 'not_joined' | 'hyosung' | 'toss_reviewing' | 'toss_general' | 'toss_auto';
export type TossOnboardingAction = 'start' | 'continue' | 'review' | 'completed';

export type AccountServiceStatusInfo = TossOnboardingInfo & {
  accountProvider?: AccountProvider;
  onboardingExists?: boolean | null;
  autoPaymentEnabled?: boolean | null;
};

export type AccountServiceStatusResult = {
  status: AccountServiceStatus;
  label: string;
  onboardingAction: TossOnboardingAction;
};

export type AccountServiceStatusButtonColor = 'account_blue' | 'light_gray' | 'account_red';

export type AccountServiceStatusButtonVisualState = {
  color: AccountServiceStatusButtonColor;
  blink: boolean;
};

export type TossOnboardingIntroLeadMessage = ReactNode;
export type TossOnboardingIntroLeadMessage2 = string;

type TossOnboardingButtonDetail = {
  text: string;
  url: string;
};

export type TossOnboardingIntroButton = {
  left: TossOnboardingButtonDetail;
  right: TossOnboardingButtonDetail;
};

export type TossOnboardingIntroBottomNotice = {
  text: string;
  color: 'red' | 'black';
  underlineText?: string;
};

export type TossOnboardingEligibilityUser = {
  account_registration?: number | string | null;
  is_teacher?: boolean | null;
  memtype?: string | null;
  open?: number | string | null;
  pretest?: number | string | null;
  uloginid?: string | null;
  useaccount?: number | string | null;
};

export type TossOnboardingIneligibleAlert = 'only_payment' | 'notice_before_open' | 'no_permission';

export type TossOnboardingResetVisibilityInfo = TossOnboardingInfo & {
  onboardingExists?: boolean | null;
};

export type TossOnboardingResetVisibilityOptions = {
  user?: TossOnboardingEligibilityUser | null;
  info?: TossOnboardingResetVisibilityInfo | null;
  enableReset?: boolean | string | null;
  nodeEnv?: string;
};

export type NormalizedTossOnboardingInfo = {
  onboardingExists: boolean;
  shopId: string;
  mid: string;
  applyUrl: string;
  applyUrlExpiresInSeconds: number;
  onboardingStatus: string;
  contractStatus: string;
  modifying: number | boolean;
  dashboardUrl: string;
  feeStatus: string;
  feePaidAt: string;
  credentialsSynced: boolean;
  credentialsSyncError: string;
  billingOnboardingExists: boolean;
  billingContractStatus: string;
  billingCredentialsSynced: boolean;
  autoPaymentEnabled: boolean;
  modalViewed: boolean;
};

export type NormalizedTossOnboardingMerchantInfo = {
  shopName: string;
  businessRegistrationNumber: string;
  shopContactName: string;
  shopContactPhoneNumber: string;
};

export type NormalizedTossOnboardingApplicationUrlInfo = Pick<
  NormalizedTossOnboardingInfo,
  'applyUrl' | 'applyUrlExpiresInSeconds'
> & {
  onboardingLoaded: boolean;
  onboardingError: string;
};

export type ReservedTossOnboardingWindow = {
  location: {
    href: string;
  };
  close: () => void;
  document?: {
    open: () => void;
    write: (html: string) => void;
    close: () => void;
  };
};

const TOSS_ONBOARDING_ERROR_MESSAGES: Record<string, string> = {
  TOSS_ONBOARDING_CONFIG_MISSING: '결제 서비스 설정이 준비되지 않았습니다. 고객센터에 문의해주세요.',
  TOSS_ONBOARDING_FORBIDDEN: '결제 서비스 가입 권한이 없습니다.',
  TOSS_ONBOARDING_NOT_FOUND: '결제 서비스 가입 신청 내역이 없습니다.',
  TOSS_CREATE_SHOP_FAILED: '토스 상점 생성에 실패했습니다. 다시 시도해주세요.',
  TOSS_OPEN_SESSION_FAILED: '토스 가입 신청 URL 발급에 실패했습니다. 다시 시도해주세요.',
  TOSS_APPLY_URL_MISSING: '토스 가입 신청 URL을 확인할 수 없습니다. 다시 시도해주세요.',
  TOSS_ONBOARDING_APPLICATION_INCOMPLETE: '토스 가입 신청 정보가 완성되지 않았습니다. 다시 신청해주세요.',
};

export const REVIEW_CONTRACT_STATUS = 'WAIT_FOR_REVIEW';
export const DONE_CONTRACT_STATUS = 'DONE';

const ACCOUNT_SERVICE_JOIN_LABEL = '가입';
const ALIMBANKING_USER = '사용중';
const TOSS_REVIEWING = '심사중';
const TOSS_PG_USER = '일반 결제';
const TOSS_BILLING_USER = '자동 결제';

export const TOSS_ONBOARDING_FALLBACK_ERROR_MESSAGE = '결제 서비스 신청에 실패했습니다. 다시 시도해주세요.';
export const TOSS_ONBOARDING_OPEN_SUCCESS_MESSAGE = '결제 서비스 페이지가 오픈되었습니다.';
export const TOSS_ONBOARDING_APPLY_URL_BUTTON_GUIDE_MESSAGE =
  '서비스 가입 신청이 완료되었습니다.\r신청 페이지로 이동하기 버튼을 눌러주세요.';
const TOSS_ONBOARDING_POPUP_NAME = 'toss_onboarding';
const TOSS_ONBOARDING_POPUP_FEATURES = 'left=0,top=0,width=700,height=800';

function isPositiveNumber(value?: number | string | null): boolean {
  return Number(value) > 0;
}

export function getTossOnboardingIneligibleAlert(
  user?: TossOnboardingEligibilityUser | null,
): TossOnboardingIneligibleAlert | null {
  if (!user) {
    return 'no_permission';
  }

  if (Number(user.open) === 2) {
    return 'notice_before_open';
  }

  if (user.memtype === 'F' || Number(user.pretest) === 1 || user.uloginid === 'pretest') {
    return 'only_payment';
  }

  if (user.is_teacher || !isPositiveNumber(user.useaccount)) {
    return 'no_permission';
  }

  return null;
}

export function canRequestTossOnboarding(user?: TossOnboardingEligibilityUser | null): boolean {
  return getTossOnboardingIneligibleAlert(user) === null;
}

function isTossOnboardingResetEnabled(enableReset?: boolean | string | null, nodeEnv = process.env.NODE_ENV): boolean {
  if (typeof enableReset === 'boolean') {
    return enableReset;
  }

  if (typeof enableReset === 'string' && enableReset.length > 0) {
    return enableReset.toLowerCase() === 'true';
  }

  return nodeEnv !== 'production';
}

function hasTossOnboardingResettableInfo(info?: TossOnboardingResetVisibilityInfo | null): boolean {
  if (!info) {
    return false;
  }

  const onboardingStatus = String(info.onboardingStatus || '').toUpperCase();

  return Boolean(
    info.onboardingExists ||
      info.exists ||
      info.shopId ||
      info.mid ||
      info.applyUrl ||
      info.contractStatus ||
      (onboardingStatus && onboardingStatus !== 'NOT_STARTED'),
  );
}

export function shouldShowTossOnboardingResetButton({
  user,
  info,
  enableReset,
}: TossOnboardingResetVisibilityOptions): boolean {
  return false;
}
export function shouldOpenTossOnboardingApplyUrlImmediately(userAgent = window.navigator.userAgent): boolean {
  const lowerUserAgent = userAgent.toLowerCase();
  const isSafari =
    lowerUserAgent.includes('safari') &&
    !lowerUserAgent.includes('chrome') &&
    !lowerUserAgent.includes('chromium') &&
    !lowerUserAgent.includes('crios') &&
    !lowerUserAgent.includes('edg/') &&
    !lowerUserAgent.includes('opr/') &&
    !lowerUserAgent.includes('firefox') &&
    !lowerUserAgent.includes('fxios');

  return !isSafari;
}

const TOSS_ONBOARDING_PENDING_HTML = `<!doctype html>
<html lang="ko">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>신청 페이지 이동</title>
    <style>
      body {
        margin: 0;
        min-height: 100vh;
        display: flex;
        align-items: center;
        justify-content: center;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
        color: #202632;
        background: #f7f8fa;
      }
      main {
        text-align: center;
      }
      strong {
        display: block;
        margin-bottom: 8px;
        font-size: 18px;
      }
      span {
        color: #6b7280;
        font-size: 14px;
      }
    </style>
  </head>
  <body>
    <main>
      <strong>토스 페이지를 준비하고 있습니다.</strong>
      <span>잠시만 기다려주세요.</span>
    </main>
  </body>
</html>`;

function writeTossOnboardingPendingPage(reservedWindow: ReservedTossOnboardingWindow): void {
  try {
    reservedWindow.document?.open();
    reservedWindow.document?.write(TOSS_ONBOARDING_PENDING_HTML);
    reservedWindow.document?.close();
  } catch (error) {
    return;
  }
}

export function openReservedTossOnboardingWindow(): ReservedTossOnboardingWindow | null {
  const reservedWindow = window.open(
    '',
    TOSS_ONBOARDING_POPUP_NAME,
    TOSS_ONBOARDING_POPUP_FEATURES,
  ) as ReservedTossOnboardingWindow | null;

  if (!reservedWindow) {
    return null;
  }

  writeTossOnboardingPendingPage(reservedWindow);
  return reservedWindow;
}

export function moveReservedTossOnboardingWindowToApplyUrl(
  reservedWindow: ReservedTossOnboardingWindow | null,
  applyUrl: string,
): boolean {
  if (!reservedWindow || !applyUrl) {
    return false;
  }

  try {
    reservedWindow.location.href = applyUrl;
    return true;
  } catch (error) {
    return false;
  }
}

export function closeReservedTossOnboardingWindow(reservedWindow: ReservedTossOnboardingWindow | null): void {
  try {
    reservedWindow?.close();
  } catch (error) {
    return;
  }
}

export function openTossOnboardingApplyUrl(applyUrl: string): boolean {
  if (!applyUrl) {
    return false;
  }

  try {
    return Boolean(window.open(applyUrl, TOSS_ONBOARDING_POPUP_NAME, TOSS_ONBOARDING_POPUP_FEATURES));
  } catch (error) {
    return false;
  }
}

export function getTossOnboardingApplyUrl(response?: TossOnboardingResponse | null): string {
  const message = response?.message;

  if (response?.pass === false || typeof message !== 'object' || message === null) {
    return '';
  }

  return typeof message.applyUrl === 'string' ? message.applyUrl : '';
}

export function getTossOnboardingErrorCode(response?: TossOnboardingResponse | null): string {
  const message = response?.message;

  return (
    response?.errorCode ||
    response?.code ||
    response?.error ||
    (typeof message === 'object' && message !== null
      ? String(message.errorCode || message.code || message.error || '')
      : '') ||
    (typeof message === 'string' && message.startsWith('TOSS_') ? message : '') ||
    ''
  );
}

export function getTossOnboardingErrorMessage(response?: TossOnboardingResponse | null): string {
  const message = response?.message;
  const code = getTossOnboardingErrorCode(response);

  if (code && TOSS_ONBOARDING_ERROR_MESSAGES[code]) {
    return TOSS_ONBOARDING_ERROR_MESSAGES[code];
  }

  if (typeof message === 'string' && message.length > 0 && !message.startsWith('TOSS_')) {
    return message;
  }

  return TOSS_ONBOARDING_FALLBACK_ERROR_MESSAGE;
}

export function normalizeTossOnboardingInfo(
  message?: string | TossOnboardingInfo | null,
): NormalizedTossOnboardingInfo {
  const info = typeof message === 'object' && message !== null ? message : {};
  const billingOnboarding = info.billingOnboarding || {};
  const hasOnboardingInfo = Boolean(
    info.exists || info.shopId || info.mid || info.onboardingStatus || info.contractStatus || info.dashboardUrl,
  );

  return {
    onboardingExists: info.exists === undefined || info.exists === null ? hasOnboardingInfo : Boolean(info.exists),
    shopId: info.shopId || '',
    mid: info.mid || '',
    applyUrl: info.applyUrl || '',
    applyUrlExpiresInSeconds: info.applyUrlExpiresInSeconds || 0,
    onboardingStatus: info.onboardingStatus || '',
    contractStatus: info.contractStatus || '',
    modifying: info.modifying || 0,
    dashboardUrl: info.dashboardUrl || '',
    feeStatus: info.feeStatus || '',
    feePaidAt: info.feePaidAt || '',
    credentialsSynced: Boolean(info.credentialsSynced),
    credentialsSyncError: info.credentialsSyncError || '',
    billingOnboardingExists: Boolean(billingOnboarding.exists),
    billingContractStatus: billingOnboarding.contractStatus || '',
    billingCredentialsSynced: Boolean(billingOnboarding.credentialsSynced),
    autoPaymentEnabled: Boolean(billingOnboarding.enabled),
    modalViewed: info.modalViewed ? info.modalViewed === 1 : false,
  };
}

export function normalizeTossOnboardingStatusResponse(
  response?: TossOnboardingResponse | null,
): NormalizedTossOnboardingInfo &
  NormalizedTossOnboardingMerchantInfo & { onboardingLoaded: boolean; onboardingError: string } {
  const info = typeof response?.message === 'object' && response.message !== null ? response.message : {};
  const merchantInfo: NormalizedTossOnboardingMerchantInfo = {
    shopName: info.shopName || '',
    businessRegistrationNumber: info.businessRegistrationNumber || '',
    shopContactName: info.shopContactName || '',
    shopContactPhoneNumber: info.shopContactPhoneNumber || '',
  };

  if (response?.pass === false) {
    return {
      ...normalizeTossOnboardingInfo(null),
      ...merchantInfo,
      onboardingLoaded: true,
      onboardingError: getTossOnboardingErrorCode(response),
    };
  }

  return {
    ...normalizeTossOnboardingInfo(response?.message),
    ...merchantInfo,
    onboardingLoaded: true,
    onboardingError: '',
  };
}

export function normalizeTossOnboardingApplicationUrlResponse(
  response?: TossOnboardingResponse | null,
): NormalizedTossOnboardingApplicationUrlInfo {
  if (response?.pass === false) {
    return {
      applyUrl: '',
      applyUrlExpiresInSeconds: 0,
      onboardingLoaded: true,
      onboardingError: getTossOnboardingErrorCode(response),
    };
  }

  const info = typeof response?.message === 'object' && response.message !== null ? response.message : {};

  return {
    applyUrl: info.applyUrl || '',
    applyUrlExpiresInSeconds: info.applyUrlExpiresInSeconds || 0,
    onboardingLoaded: true,
    onboardingError: '',
  };
}

function normalizeContractStatus(contractStatus?: string | null): string {
  return String(contractStatus || '').toUpperCase();
}

function normalizeOpenStatus(open?: number | string | null): number {
  return Number(open);
}

function getOnboardingAction(info: AccountServiceStatusInfo): TossOnboardingAction {
  if (info.accountProvider === 'TOSS') return 'completed';
  if (info.contractStatus === REVIEW_CONTRACT_STATUS) return 'review';
  if (info.shopId && info.mid) return 'continue';
  return 'start';
}
export function getAccountServiceStatus(info: AccountServiceStatusInfo): AccountServiceStatusResult {
  const onboardingAction = getOnboardingAction(info);

  if (info.accountProvider === 'HYOSUNG') {
    if (onboardingAction === 'review') {
      return { status: 'toss_reviewing', label: TOSS_REVIEWING, onboardingAction };
    }

    return { status: 'hyosung', label: ALIMBANKING_USER, onboardingAction };
  }

  if (info.accountProvider === 'TOSS') {
    return {
      status: info.autoPaymentEnabled ? 'toss_auto' : 'toss_general',
      label: info.autoPaymentEnabled ? TOSS_BILLING_USER : TOSS_PG_USER,
      onboardingAction,
    };
  }

  if (onboardingAction === 'review') {
    return { status: 'toss_reviewing', label: TOSS_REVIEWING, onboardingAction };
  }

  return { status: 'not_joined', label: ACCOUNT_SERVICE_JOIN_LABEL, onboardingAction };
}

export function getAccountServiceStatusButtonVisualState({
  showJoinButton,
  open,
  contractStatus,
}: {
  showJoinButton: boolean;
  open?: number | string | null;
  contractStatus?: string | null;
}): AccountServiceStatusButtonVisualState {
  const normalizedContractStatus = normalizeContractStatus(contractStatus);
  const normalizedOpen = normalizeOpenStatus(open);

  if (normalizedOpen === 1) {
    return { color: 'account_red', blink: false };
  }

  if (normalizedOpen === 2 || normalizedContractStatus === REVIEW_CONTRACT_STATUS) {
    return { color: 'account_red', blink: false };
  }

  return {
    color: 'account_red',
    blink: showJoinButton,
  };
}

export function getTossOnboardingIntroLeadMessage(contractStatus?: string | null): TossOnboardingIntroLeadMessage {
  const normalizedContractStatus = normalizeContractStatus(contractStatus);

  if (normalizedContractStatus === REVIEW_CONTRACT_STATUS) {
    return (
      <span className='red' style={{ fontWeight: 'bold' }}>
        현재 카드사에서 심사를 진행중입니다. (영업일 기준 약 1~2주일 소요)
      </span>
    );
  }

  if (normalizedContractStatus === DONE_CONTRACT_STATUS) {
    return (
      <span className='red'>
        현재 <span className='text_underline red'>일반결제(일회성)</span>가 가능한 상태입니다.
      </span>
    );
  }

  return <span>학원비 결제를 더 편리하게 관리하세요!</span>;
}

export function getTossOnboardingIntroLeadMessage2(contractStatus?: string | null): TossOnboardingIntroLeadMessage2 {
  const normalizedContractStatus = normalizeContractStatus(contractStatus);

  if ([REVIEW_CONTRACT_STATUS, DONE_CONTRACT_STATUS].includes(normalizedContractStatus)) {
    return '수강료 결제 서비스 "사용자 매뉴얼"을 필독해주세요.';
  }

  return '서비스 가입 전 아래 사용자 매뉴얼을 먼저 확인해주세요.';
}

export function getTossOnboardingIntroButnonText(
  contractStatus?: string | null,
  open?: number,
): TossOnboardingIntroButton {
  const normalizedContractStatus = normalizeContractStatus(contractStatus);
  if ([REVIEW_CONTRACT_STATUS, DONE_CONTRACT_STATUS].includes(normalizedContractStatus)) {
    return {
      left: {
        text: '사용자 매뉴얼 PDF',
        url: LINK_ALL_METHOD_PDF,
      },
      right: {
        text: '사용자 매뉴얼 영상',
        url: LINK_PAY_METHOD_VIDEO,
      },
    };
  }

  return {
    left: {
      text: '변경 안내영상',
      url: [1, 2].includes(open ? open : 0) ? LINK_BILLING_METHOD_VIDEO_HYOSUNG : LINK_BILLING_METHOD_VIDEO,
    },
    right: {
      text: '토스페이먼츠 가입 절차',
      url: LINK_BILLING_METHOD_PDF,
    },
  };
}

export function getTossOnboardingIntroBottomNotice({
  open,
  contractStatus,
}: {
  open?: number | string | null;
  contractStatus?: string | null;
}): TossOnboardingIntroLeadMessage {
  const normalizedContractStatus = normalizeContractStatus(contractStatus);
  const normalizedOpen = normalizeOpenStatus(open);
  let el = (
    <span className='red'>
      신규 선생님께서는 신청 후{' '}
      <span className='text_underline red'>토스페이먼츠의 가입완료 안내를 받으신 후 사용 가능</span>해요!
    </span>
  );

  if ([REVIEW_CONTRACT_STATUS, DONE_CONTRACT_STATUS].includes(normalizedContractStatus)) {
    el = <span>더욱 강력해진 수강료 결제, 이제 출결버스와 함께하세요!</span>;
  } else if (normalizedOpen === 1 || normalizedOpen === 2) {
    el = (
      <span className='red'>
        효성 알림뱅킹을 이용중인 선생님께서는 <span className='text_underline red'>토스페이먼츠로 신규 가입 </span>
        부탁드립니다.
      </span>
    );
  }

  return <Box sx={{ mt: '1rem' }}>{el}</Box>;
}
